import type {
  InfiniteData,
  Query,
  QueryClient,
  QueryKey,
} from "@tanstack/react-query";
import {
  CONFIGURE_DEBOUNCE_MS,
  CONFIGURE_READY_TIMEOUT_MS,
  CONTENT_HUB_ADAPTER_URL,
  CONTENT_HUB_EVENTS_KEY,
  CONTENT_HUB_SEARCH_KEY,
  MAX_FIRST_PAGE,
  contentHubUrl,
} from "./config";
import {
  ContentHubConfigureError,
  ContentHubConnectionGoneError,
  configureContentHubStream,
  type StreamFilterConfig,
} from "./api";
import { fetchStreamPassUrl } from "./client";
import type { FilterTreeNode } from "./filter";
import { mapStreamFrame } from "./mappers";
import type {
  ContentHubConnectedFrame,
  ContentHubItem,
  ContentHubMatchFrame,
  ContentHubPage,
  ContentHubStreamFrame,
  ContentHubStreamStatus,
} from "./types";

type EventsInfiniteData = InfiniteData<ContentHubPage>;

/** Settlement status of a live filter, used to gate its search query so the
 *  view fetches page one only after `configure` (or a fallback). */
export type FilterStatus = "pending" | "ready" | "errored" | "unknown";

interface DesiredFilter {
  tree: FilterTreeNode;
  queryKey: QueryKey;
  refCount: number;
}

/**
 * Prepend/upsert a live item into an infinite-query cache: replace in place on
 * any page if already present (an update/correction), else prepend to the first
 * (newest) page, capped so a live burst can't grow it without bound. Shared by
 * unfiltered `all_events` patching and filtered `match` patching.
 */
function patchInfinitePages(
  old: EventsInfiniteData | undefined,
  item: ContentHubItem
): EventsInfiniteData | undefined {
  if (!old || old.pages.length === 0) return old;

  for (let p = 0; p < old.pages.length; p++) {
    const i = old.pages[p].items.findIndex(
      (e) => e.publishId === item.publishId
    );
    if (i >= 0) {
      const pages = [...old.pages];
      const items = [...pages[p].items];
      items[i] = { ...item, wasUpdated: true };
      pages[p] = { ...pages[p], items };
      return { ...old, pages };
    }
  }

  const first = old.pages[0];
  const pages = [
    { ...first, items: [item, ...first.items].slice(0, MAX_FIRST_PAGE) },
    ...old.pages.slice(1),
  ];
  return { ...old, pages };
}

/** Match any base-feed events query whose (sorted) source list contains the
 *  given backend_source, so one frame patches every view that includes it
 *  (single-source widgets and the combined stocks feed alike). */
const eventsQueryMatches =
  (backendSource: string) =>
  (query: Query): boolean => {
    const key = query.queryKey;
    return (
      Array.isArray(key) &&
      key[0] === CONTENT_HUB_EVENTS_KEY[0] &&
      key[1] === CONTENT_HUB_EVENTS_KEY[1] &&
      Array.isArray(key[2]) &&
      (key[2] as string[]).includes(backendSource)
    );
  };

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const CONFIGURE_RETRY_DELAY_MS = 2_000;
/** How long after the socket opens to wait for a `connected` data frame before
 *  concluding the adapter doesn't support live filtering (older deployments
 *  send only an SSE `: connected` comment). Determined once at startup, so
 *  later filter activations gate with no artificial delay. */
const HANDSHAKE_TIMEOUT_MS = 1_000;
/** Lifetime of a one-time stream pass. If the socket hasn't gone live within
 *  this window of receiving a pass, the pass is dead — discard the attempt and
 *  restart the two-step exchange with a fresh pass. Matches the adapter's TTL. */
const STREAM_PASS_TTL_MS = 30_000;
/** Grace period before closing the connection once the last subscriber
 *  unmounts — absorbs React effect unmount/remount cycles (StrictMode,
 *  route transitions) without churning the SSE connection. */
const SHUTDOWN_DELAY_MS = 3_000;

/**
 * Owns the single shared SSE connection to GET /v1/stream?feed=all.
 *
 * Unfiltered feeds: `all_events` frames are routed by backend_source into every
 * matching base-feed query. Live filtered feeds: active filters are registered
 * server-side via POST /v1/stream/configure (replace semantics, keyed by the
 * client filter id) and their matches arrive as `match` frames on the same
 * socket, patched into the filter's search cache. The connection is
 * ref-counted; reconnects use capped exponential backoff and, on a new
 * connection_id, re-configure the desired filter set before recovering.
 */
class ContentHubStreamManager {
  private eventSource: EventSource | null = null;
  /** Guards against overlapping connects while the pass exchange is in flight
   *  (connect() is async), so a re-entrant subscribe/reconnect can't open a
   *  second socket. */
  private connecting = false;
  /** Watchdog for the one-time pass's TTL: fires if the socket doesn't go live
   *  before the pass expires, triggering a fresh two-step reconnect. */
  private passTimer: ReturnType<typeof setTimeout> | null = null;
  private queryClient: QueryClient | null = null;
  private subscriberCount = 0;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shutdownTimer: ReturnType<typeof setTimeout> | null = null;
  private hasConnectedOnce = false;
  private status: ContentHubStreamStatus = "idle";
  private statusListeners = new Set<() => void>();

  // ── Live-filter state ──────────────────────────────────────────────────
  private connectionId: string | null = null;
  /** null = unknown, true = adapter sent a `connected` frame, false = it didn't
   *  (older deployment) so live filtering is unavailable and gating is skipped. */
  private liveSupported: boolean | null = null;
  private handshakeTimer: ReturnType<typeof setTimeout> | null = null;
  private desired = new Map<string, DesiredFilter>();
  /** Ids the adapter rejected (422); excluded from configure until re-registered. */
  private erroredIds = new Set<string>();
  private filterStatus = new Map<string, FilterStatus>();
  private filterStatusListeners = new Set<() => void>();
  private readyTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private configureTimer: ReturnType<typeof setTimeout> | null = null;
  private configureRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private configureInFlight = false;
  private configureDirty = false;
  /** Id-set last successfully sent, to skip no-op configure calls. */
  private lastConfiguredKey = "";

  /** Register a subscriber. Starts the connection if it's the first one.
   *  Returns an unsubscribe function. */
  subscribe(queryClient: QueryClient): () => void {
    this.queryClient = queryClient;
    this.subscriberCount += 1;

    if (this.shutdownTimer) {
      clearTimeout(this.shutdownTimer);
      this.shutdownTimer = null;
    }
    if (!this.eventSource && !this.reconnectTimer && !this.connecting) {
      void this.connect();
    }

    let unsubscribed = false;
    return () => {
      if (unsubscribed) return;
      unsubscribed = true;
      this.subscriberCount -= 1;
      if (this.subscriberCount <= 0) {
        this.scheduleShutdown();
      }
    };
  }

  /**
   * Register a live filter (ref-counted). The caller must also `subscribe` to
   * keep the socket alive. Returns an unregister that drops the filter and
   * reconfigures the desired set.
   */
  registerFilter(id: string, tree: FilterTreeNode, queryKey: QueryKey): () => void {
    const existing = this.desired.get(id);
    if (existing) {
      existing.refCount += 1;
      existing.tree = tree;
      existing.queryKey = queryKey;
    } else {
      this.desired.set(id, { tree, queryKey, refCount: 1 });
      // Gate the query until configure resolves — unless the adapter isn't
      // configured, or we've already determined it doesn't support live
      // filtering, in which case fetch immediately (non-live search).
      const gate = CONTENT_HUB_ADAPTER_URL && this.liveSupported !== false;
      this.setFilterStatus(id, gate ? "pending" : "ready");
      if (gate) this.armReadyTimeout(id);
    }
    this.scheduleConfigure();

    let unregistered = false;
    return () => {
      if (unregistered) return;
      unregistered = true;
      const entry = this.desired.get(id);
      if (!entry) return;
      entry.refCount -= 1;
      if (entry.refCount <= 0) {
        this.desired.delete(id);
        this.erroredIds.delete(id);
        this.clearReadyTimeout(id);
        this.filterStatus.delete(id);
        this.scheduleConfigure();
      }
    };
  }

  /** Current settlement status of a filter (for the query gate). */
  getFilterStatus = (id: string): FilterStatus =>
    this.filterStatus.get(id) ?? "unknown";

  /** Subscribe to filter-status changes (useSyncExternalStore-compatible). */
  onFilterStatusChange = (listener: () => void): (() => void) => {
    this.filterStatusListeners.add(listener);
    return () => this.filterStatusListeners.delete(listener);
  };

  getStatus = (): ContentHubStreamStatus => this.status;

  /** Subscribe to status changes (useSyncExternalStore-compatible). */
  onStatusChange = (listener: () => void): (() => void) => {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  };

  private setStatus(status: ContentHubStreamStatus) {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((listener) => listener());
  }

  private setFilterStatus(id: string, status: FilterStatus) {
    if (this.filterStatus.get(id) === status) return;
    this.filterStatus.set(id, status);
    this.filterStatusListeners.forEach((listener) => listener());
  }

  /** After the fallback timeout, let a still-pending filter fetch anyway so a
   *  slow/configure-less adapter degrades to non-live search rather than hang. */
  private armReadyTimeout(id: string) {
    this.clearReadyTimeout(id);
    this.readyTimers.set(
      id,
      setTimeout(() => {
        this.readyTimers.delete(id);
        if (this.filterStatus.get(id) === "pending") {
          this.setFilterStatus(id, "ready");
        }
      }, CONFIGURE_READY_TIMEOUT_MS)
    );
  }

  private clearReadyTimeout(id: string) {
    const t = this.readyTimers.get(id);
    if (t) {
      clearTimeout(t);
      this.readyTimers.delete(id);
    }
  }

  /** Release every still-pending gate — used once we conclude the adapter has
   *  no live-filtering support, so filtered views fall back to plain search. */
  private releasePendingGates() {
    for (const [id, status] of this.filterStatus) {
      if (status === "pending") {
        this.clearReadyTimeout(id);
        this.setFilterStatus(id, "ready");
      }
    }
  }

  private async connect() {
    if (!CONTENT_HUB_ADAPTER_URL) return; // adapter not configured
    if (this.connecting || this.eventSource) return; // already (re)connecting
    this.connecting = true;
    this.clearReconnectTimer();
    this.setStatus(this.hasConnectedOnce ? "reconnecting" : "connecting");

    let eventSource: EventSource;
    try {
      // Step 1: exchange the user's JWT for a short-lived, single-use stream
      // pass. Runs on every (re)connect because the pass is one-time-use.
      // feed=all keeps the firehose all_events frames flowing alongside matches.
      const url = await fetchStreamPassUrl(
        contentHubUrl("v1/stream") + "?feed=all"
      );
      // A shutdown (last subscriber left) may have raced the pass exchange —
      // don't open a stray socket.
      if (this.subscriberCount <= 0 || this.eventSource) return;

      // Step 2: open the SSE stream with the pass.
      eventSource = new EventSource(url);
      this.eventSource = eventSource;
    } catch {
      // The pass exchange failed (network / auth / no pass). Back off and retry
      // the whole two-step flow.
      this.eventSource = null;
      this.setStatus("reconnecting");
      this.scheduleReconnect();
      return;
    } finally {
      this.connecting = false;
    }

    // Arm the pass-TTL watchdog: the pass is consumed when the connection goes
    // live, so if the socket doesn't open before it expires, restart with a
    // fresh pass instead of waiting on a connection that can't authenticate.
    this.armPassTimeout(eventSource);

    eventSource.onopen = () => {
      this.clearPassTimeout(); // socket live — the pass was consumed successfully
      this.reconnectAttempt = 0;
      this.setStatus("connected");
      // The connection_id + reconnect recovery are driven by the `connected`
      // frame (below), not onopen, so configure precedes the recovery refetch.
      // If no `connected` frame arrives shortly, the adapter is an older build
      // without live filtering — stop gating so filtered views fetch promptly.
      if (this.liveSupported === null && !this.handshakeTimer) {
        this.handshakeTimer = setTimeout(() => {
          this.handshakeTimer = null;
          if (this.liveSupported === null) {
            this.liveSupported = false;
            this.releasePendingGates();
          }
        }, HANDSHAKE_TIMEOUT_MS);
      }
    };

    eventSource.onmessage = (event: MessageEvent<string>) => {
      this.handleFrame(event.data);
    };

    eventSource.onerror = () => {
      // Manage reconnection ourselves (capped exponential backoff) instead
      // of relying on EventSource's fixed-interval auto-retry.
      this.clearPassTimeout();
      eventSource.close();
      if (this.eventSource !== eventSource) return;
      this.eventSource = null;
      this.connectionId = null; // old connection's server-side filters are gone
      this.setStatus("reconnecting");
      this.scheduleReconnect();
    };
  }

  /** Start the pass-TTL watchdog for a just-opened socket. If it fires before
   *  the socket goes live (onopen), the one-time pass has expired unused —
   *  discard this attempt and restart the two-step exchange. */
  private armPassTimeout(eventSource: EventSource) {
    this.clearPassTimeout();
    this.passTimer = setTimeout(() => {
      this.passTimer = null;
      if (this.eventSource !== eventSource) return; // already superseded/closed
      eventSource.close();
      this.eventSource = null;
      this.connectionId = null;
      this.setStatus("reconnecting");
      this.scheduleReconnect();
    }, STREAM_PASS_TTL_MS);
  }

  private clearPassTimeout() {
    if (this.passTimer) {
      clearTimeout(this.passTimer);
      this.passTimer = null;
    }
  }

  private handleFrame(data: string) {
    let frame: { type?: string };
    try {
      frame = JSON.parse(data);
    } catch {
      return; // malformed frame — ignore
    }
    if (!frame || typeof frame.type !== "string") return;

    // The top-level frame and the `all_events` envelope share a `type: string`
    // field, so narrow by explicit cast per branch rather than union inference.
    switch (frame.type) {
      case "connected":
        this.onConnected((frame as ContentHubConnectedFrame).connection_id);
        return;
      case "match": {
        const m = frame as ContentHubMatchFrame;
        this.onMatch(m.filter_ids, m.event);
        return;
      }
      case "all_events":
        this.onAllEvents(frame as ContentHubStreamFrame);
        return;
      default:
        return;
    }
  }

  /** Handshake / reconnect: capture the connection id, (re)configure the
   *  desired set, then run reconnect recovery once configure settles. */
  private onConnected(connectionId: string) {
    this.connectionId = connectionId;
    this.liveSupported = true;
    if (this.handshakeTimer) {
      clearTimeout(this.handshakeTimer);
      this.handshakeTimer = null;
    }
    const isReconnect = this.hasConnectedOnce;
    this.hasConnectedOnce = true;
    // A fresh connection has no server-side filters; force a resend.
    this.lastConfiguredKey = "";

    const recover = () => {
      if (!isReconnect) return;
      // Reset both unfiltered and filtered queries to a fresh page one; the
      // reconnect gap is at the head of both feeds.
      this.queryClient?.resetQueries({ queryKey: CONTENT_HUB_EVENTS_KEY });
      this.queryClient?.resetQueries({ queryKey: CONTENT_HUB_SEARCH_KEY });
    };

    if (this.desired.size > 0) {
      this.flushConfigure().finally(recover);
    } else {
      recover();
    }
  }

  private onAllEvents(frame: ContentHubStreamFrame) {
    if (!frame.backend_source) return;
    const item = mapStreamFrame(frame);
    item.receivedAt = Date.now();

    this.queryClient?.setQueriesData<EventsInfiniteData>(
      { predicate: eventsQueryMatches(frame.backend_source) },
      (old) => patchInfinitePages(old, item)
    );
  }

  private onMatch(filterIds: string[], event: ContentHubStreamFrame) {
    if (!Array.isArray(filterIds) || filterIds.length === 0 || !event) return;
    const item = mapStreamFrame(event);
    item.receivedAt = Date.now();

    for (const id of filterIds) {
      const entry = this.desired.get(id);
      if (!entry) continue; // raced a reconfigure — drop silently
      this.queryClient?.setQueryData<EventsInfiniteData>(entry.queryKey, (old) =>
        patchInfinitePages(old, item)
      );
    }
  }

  // ── configure lifecycle ────────────────────────────────────────────────

  private scheduleConfigure() {
    if (this.configureTimer) clearTimeout(this.configureTimer);
    this.configureTimer = setTimeout(() => {
      this.configureTimer = null;
      void this.flushConfigure();
    }, CONFIGURE_DEBOUNCE_MS);
  }

  private currentFilterConfigs(): StreamFilterConfig[] {
    const configs: StreamFilterConfig[] = [];
    for (const [id, entry] of this.desired) {
      if (this.erroredIds.has(id)) continue; // exclude rejected trees
      configs.push({ id, tree: entry.tree });
    }
    return configs;
  }

  private async flushConfigure(): Promise<void> {
    if (!this.connectionId) return; // no socket yet — the connected frame flushes
    if (this.configureInFlight) {
      this.configureDirty = true;
      return;
    }

    const filters = this.currentFilterConfigs();
    const key = filters
      .map((f) => f.id)
      .sort()
      .join(",");
    if (key === this.lastConfiguredKey) return; // no change since last ack

    this.configureInFlight = true;
    const connectionId = this.connectionId;
    try {
      await configureContentHubStream(connectionId, filters);
      this.lastConfiguredKey = key;
      // Everything we sent is now active; anything not sent (errored) stays so.
      for (const f of filters) this.setFilterStatus(f.id, "ready");
      this.resetConfigureRetry();
    } catch (err) {
      if (err instanceof ContentHubConfigureError) {
        // One bad tree: mark it errored (so its query stops gating and surfaces
        // the same tree error via /v1/search), keep the rest, resend without it.
        const badId = err.filterId;
        if (badId && this.desired.has(badId)) {
          this.erroredIds.add(badId);
          this.setFilterStatus(badId, "errored");
        } else {
          // Adapter didn't name the id — release all gates to avoid a hang.
          for (const f of filters) this.setFilterStatus(f.id, "ready");
        }
        this.lastConfiguredKey = ""; // force the corrected resend
        this.configureDirty = true;
      } else if (err instanceof ContentHubConnectionGoneError) {
        // Connection died before we noticed; the reconnect path reconfigures.
        this.connectionId = null;
        this.lastConfiguredKey = "";
      } else {
        // Network/5xx — retry while the socket is alive (idempotent resend).
        this.scheduleConfigureRetry();
      }
    } finally {
      this.configureInFlight = false;
      if (this.configureDirty) {
        this.configureDirty = false;
        void this.flushConfigure();
      }
    }
  }

  private scheduleConfigureRetry() {
    if (this.configureRetryTimer) return;
    this.configureRetryTimer = setTimeout(() => {
      this.configureRetryTimer = null;
      if (this.connectionId && this.subscriberCount > 0) {
        void this.flushConfigure();
      }
    }, CONFIGURE_RETRY_DELAY_MS);
  }

  private resetConfigureRetry() {
    if (this.configureRetryTimer) {
      clearTimeout(this.configureRetryTimer);
      this.configureRetryTimer = null;
    }
  }

  private scheduleReconnect() {
    this.clearReconnectTimer();
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempt,
      MAX_RECONNECT_DELAY_MS
    );
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.subscriberCount > 0) void this.connect();
    }, delay);
  }

  private scheduleShutdown() {
    if (this.shutdownTimer) return;
    this.shutdownTimer = setTimeout(() => {
      this.shutdownTimer = null;
      if (this.subscriberCount > 0) return;
      this.clearReconnectTimer();
      this.resetConfigureRetry();
      if (this.configureTimer) {
        clearTimeout(this.configureTimer);
        this.configureTimer = null;
      }
      if (this.handshakeTimer) {
        clearTimeout(this.handshakeTimer);
        this.handshakeTimer = null;
      }
      this.clearPassTimeout();
      this.eventSource?.close();
      this.eventSource = null;
      this.connectionId = null;
      this.liveSupported = null;
      this.lastConfiguredKey = "";
      this.reconnectAttempt = 0;
      this.hasConnectedOnce = false;
      this.setStatus("idle");
    }, SHUTDOWN_DELAY_MS);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

export const contentHubStreamManager = new ContentHubStreamManager();
